import React, { useEffect, useMemo, useState } from 'react';
import { ValidateInput } from '@fx-ui/jdy-design-biz';
import { IconFont } from '~/components';
import classNames from 'classnames';
import { forEach, get, getPhoneByValue, includes, some } from '~/utils';
import Intl from '@fx-ui/intl';
import { PASSWORD_REG } from '~/constants';

// 密码默认最小长度为8
const DEFAULT_PASSWORD_MIN_LENGTH = 8;

/**
 * 根据带邮箱后缀的邮箱地址获取其中邮箱部分
 * 比如由"liaw@139.com"获取"liaw"
 *
 * @param value 带邮箱后缀的邮箱地址
 */
const getEmailByValue = (value: string) => {
    // 邮箱后缀分隔符
    const EMAIL_DELIMITER = '@';

    if (value) {
        const emailDelimiterIndex = value.lastIndexOf(EMAIL_DELIMITER);
        if (emailDelimiterIndex >= 0) {
            return value.slice(0, emailDelimiterIndex);
        }
        return value;
    }
    return '';
};

enum PasswordStrategyKeys {
    PASSWORD_LENGTH = 'password_length', // 密码长度限制
    DEFAULT_RULE = 'default_rule', // 默认校验规则（数字、大写字母、小写字母及特殊字符，至少包含上述字符中的三种）
    INCLUDE_NUMBER = 'include_number', // 包含数字
    INCLUDE_LOWERCASE = 'include_lowercase', // 包含小写字母
    INCLUDE_UPPERCASE = 'include_uppercase', // 包含大写字母
    INCLUDE_CHARACTER = 'include_character', // 包含特殊字符
    EXCLUDE_USER_INFO = 'exclude_user_info' // 不包含用户名（username）、手机号或邮箱
}

interface PasswordStrategies {
    key: PasswordStrategyKeys;
    text: string;
    validate: (password: string) => boolean;
}

interface ILoginPasswordProps {
    className?: string;
    value?: string;
    waterMark?: string;
    disabled?: boolean;
    valid?: boolean;
    onBlur?: (value: string, valid: boolean) => void;
    userInfo?: {
        username?: string;
        phone?: string;
        email?: string;
    };
}

// 登录密码输入组件

const LoginPassword: React.FunctionComponent<ILoginPasswordProps> = (
    props: ILoginPasswordProps
) => {
    const { className, value = '', waterMark, disabled, valid = true, onBlur, userInfo } = props;
    const [passwordValue, setPasswordValue] = useState<string>(value);
    // 密码校验
    const [passwordValid, setPasswordValid] = useState<boolean>(valid);
    const [showPassword, setShowPassword] = useState(false);

    useEffect(() => {
        setPasswordValid(valid);
    }, [valid]);

    const passwordStrategies = useMemo(() => {
        const customCheckEnabled = get(window.jdy_password_strength, 'custom_check_enabled', false);
        const strategies: PasswordStrategies[] = [];

        // 自定义密码校验配置开启时则按用户配置来（独享版）
        if (customCheckEnabled) {
            const passwordMinLength = get(
                window.jdy_password_strength,
                'password_length',
                DEFAULT_PASSWORD_MIN_LENGTH
            );
            strategies.push({
                key: PasswordStrategyKeys.PASSWORD_LENGTH,
                text: Intl.get('account.password.custom.length', { min: passwordMinLength }),
                validate: (password: string) => {
                    return new RegExp(`^.{${passwordMinLength},30}$`).test(password);
                }
            });

            const passwordCompose = get(window.jdy_password_strength, 'password_compose', []);
            forEach(passwordCompose, (item: PasswordStrategyKeys) => {
                switch (item) {
                    case PasswordStrategyKeys.INCLUDE_NUMBER:
                        strategies.push({
                            key: item,
                            text: Intl.get('account.password.custom.include.number'),
                            validate: (password: string) => {
                                return new RegExp(PASSWORD_REG.INCLUDE_NUMBER).test(password);
                            }
                        });
                        break;
                    case PasswordStrategyKeys.INCLUDE_LOWERCASE:
                        strategies.push({
                            key: item,
                            text: Intl.get('account.password.custom.include.lowercase'),
                            validate: (password: string) => {
                                return new RegExp(PASSWORD_REG.INCLUDE_LOWERCASE).test(password);
                            }
                        });
                        break;
                    case PasswordStrategyKeys.INCLUDE_UPPERCASE:
                        strategies.push({
                            key: item,
                            text: Intl.get('account.password.custom.include.uppercase'),
                            validate: (password: string) => {
                                return new RegExp(PASSWORD_REG.INCLUDE_UPPERCASE).test(password);
                            }
                        });
                        break;
                    case PasswordStrategyKeys.INCLUDE_CHARACTER:
                        strategies.push({
                            key: item,
                            text: Intl.get('account.password.custom.include.character'),
                            validate: (password: string) => {
                                return new RegExp(PASSWORD_REG.INCLUDE_CHARACTER).test(password);
                            }
                        });
                        break;
                    case PasswordStrategyKeys.EXCLUDE_USER_INFO:
                        strategies.push({
                            key: item,
                            text: Intl.get('account.password.custom.exclude.user'),
                            validate: (password: string) => {
                                const { username = '', phone = '', email = '' } = userInfo || {};
                                const phoneVal = getPhoneByValue(phone);
                                const emailVal = getEmailByValue(email);
                                const includeUserInfo = some(
                                    [username, phoneVal, emailVal],
                                    (info) => {
                                        return info && includes(password, info);
                                    }
                                );
                                return !includeUserInfo;
                            }
                        });
                        break;
                }
            });
            return strategies;
        }

        // 自定义密码校验配置未开启时则按原来的默认配置来（公有云&jodoo）
        return [
            {
                key: PasswordStrategyKeys.PASSWORD_LENGTH,
                text: Intl.get('account.password.custom.length', {
                    min: DEFAULT_PASSWORD_MIN_LENGTH
                }),
                validate: (password: string) => {
                    return new RegExp(PASSWORD_REG.PASSWORD_LENGTH).test(password);
                }
            },
            {
                key: PasswordStrategyKeys.DEFAULT_RULE,
                text: Intl.get('account.password.standard'),
                validate: (password: string) => {
                    return new RegExp(PASSWORD_REG.STANDARD).test(password);
                }
            }
        ];
    }, []);

    return (
        <ValidateInput
            validators={passwordStrategies}
            className={classNames('fx-login-password', className)}
            type={showPassword ? 'text' : 'password'}
            value={passwordValue}
            placeholder={waterMark}
            disabled={disabled}
            style={{
                width: '100%'
            }}
            onChange={(val, isValid) => {
                setPasswordValue(val);
                setPasswordValid(isValid);
            }}
            onBlur={() => {
                if (onBlur) {
                    onBlur(passwordValue, passwordValid);
                }
            }}
            suffix={
                <IconFont
                    style={{ fontSize: 18 }}
                    type={showPassword ? 'eye-close' : 'eye-open'}
                    onClick={() =>
                        setShowPassword((v) => {
                            return !v;
                        })
                    }
                />
            }
        />
    );
};

export default LoginPassword;
